import type { OrderDaySetting, OrderHistoryRow, VendorOffer, VendorSheet, VendorSheetLine, VendorSheets } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, newIdempotencyKey } from '../api';
import { useAuth } from '../auth';
import { PeriodBar, presets, usePeriod } from '../components/PeriodBar';
import { type PickOption, Sheet, type SheetColumn } from '../components/Sheet';
import { byNumber, byText, useSort } from '../components/useSort';
import { Empty, ErrorBox, QueryView } from '../components/ui';
import { addDays, formatDate, formatMoney, formatQty, isIsoDate, loadName, todayIso, weekdayName } from '../format';
import { useLeaveGuard } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { OrderDayPage } from './OrderDayPage';
import { parseNumber } from './StockPage';

/** A load's address: its name in lower case (/order-day/tuesday). */
export const loadSlug = (l: OrderDaySetting) => l.name.trim().toLowerCase().replace(/\s+/g, '-');
const round3 = (n: number) => Math.round(n * 1000) / 1000;
const isOpen = (s: VendorSheet['status']) => s === null || s === 'draft';
const isPlaced = (s: VendorSheet['status']) => s === 'sent' || s === 'confirmed';
const isDone = (s: VendorSheet['status']) => s === 'received' || s === 'closed';

/** When a placed order went out, in Denver time: "Tue, Oct 6, 11:40 AM". */
export function placedLabel(iso: string, locale: string): string {
  return new Date(iso).toLocaleString(locale, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Denver' });
}

/** /order-day: the next load's page. */
export function OrderDayHome() {
  const loads = useQuery({ queryKey: qk.orderDays, queryFn: ({ signal }) => api.orderDays(signal), staleTime: 10 * 60_000 });
  const day = useQuery({ queryKey: qk.orderDay(`${todayIso()}:next`), queryFn: ({ signal }) => api.orderDay({ date: todayIso() }, signal) });
  const next = loads.data?.find((l) => l.weekday === day.data?.load.weekday) ?? loads.data?.[0];
  return next && (day.data || day.isError) ? <Navigate to={`/order-day/${loadSlug(next)}`} replace /> : <QueryView query={loads}>{() => null}</QueryView>;
}

/**
 * One load (Tuesday, Friday), the way JL's Order Guide works (decision 76): the PLAN tab with the stores' columns and
 * the model's ORDER, then a tab per vendor to order from (MODEL says, ORDER, PLACE ORDER, RECEIVED, SHORT).
 */
export function LoadPage() {
  const { t, intlLocale } = useLocale();
  const { load: slug = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const raw = params.get('date');
  const date = raw && isIsoDate(raw) ? raw : todayIso();
  const tab = params.get('tab') ?? 'plan';
  // the call-ahead columns: absent = the customers with an order in the window, '' = none chosen
  const extra = params.get('extra');
  const loads = useQuery({ queryKey: qk.orderDays, queryFn: ({ signal }) => api.orderDays(signal), staleTime: 10 * 60_000 });
  const load = loads.data?.find((l) => loadSlug(l) === slug);
  const weekday = load?.weekday ?? 0;
  const day = useQuery({ queryKey: qk.orderDay(`${date}:${weekday}:${extra ?? 'auto'}`), queryFn: ({ signal }) => api.orderDay({ date, load: weekday, extra }, signal), enabled: !!load });
  const sheets = useQuery({ queryKey: qk.vendorSheets(`${date}:${weekday}`), queryFn: ({ signal }) => api.orderDayVendors({ date, load: weekday }, signal), enabled: !!load });
  // the call-ahead columns are kept with the load (decision 154), not in the address: the next visit shows them again
  const keepCallAheads = async (ids: string[]) => {
    await api.saveCallAheads(weekday, ids);
    const p = new URLSearchParams(params);
    p.delete('extra');
    setParams(p, { replace: true });
    await qc.invalidateQueries({ queryKey: ['forecast'] });
  };
  const set = (next: { date?: string; tab?: string }) => {
    const p = new URLSearchParams(params);
    if (next.date !== undefined) {
      p.set('date', next.date);
      p.delete('extra'); // another load: its own call-aheads
    }
    if (next.tab !== undefined) {
      if (next.tab === 'plan') p.delete('tab');
      else p.set('tab', next.tab);
    }
    setParams(p);
  };
  if (loads.data && !load) return <Navigate to="/order-day" replace />;
  const d = day.data;
  // a tab per vendor of the plan's items: the ones the model wants something from (or with an order) first
  const wants = (v: VendorSheet) => !!v.purchaseOrderId || v.lines.some((l) => l.model > 0 || l.offer !== null);
  const vendors = (sheets.data?.vendors ?? []).filter((v) => v.lines.length > 0 || v.purchaseOrderId).sort((a, b) => Number(wants(b)) - Number(wants(a)));
  const current = tab === 'plan' ? null : (sheets.data?.vendors.find((v) => v.vendorId === tab) ?? null);
  const mark = (v: VendorSheet) => (isDone(v.status) ? ` · ${t('vsReceived')}` : isPlaced(v.status) ? ' ✓' : v.status === 'draft' ? ' •' : '');

  return (
    <div className="desk-page">
      <header className="desk-head">
        <div>
          <h1>{load ? t('loadOrderTitle').replace('{load}', loadName(load, intlLocale)) : t('orderDayTitle')}</h1>
          {d ? (
            <p className="muted" data-testid="order-day-window">
              {t('lands')} {formatDate(d.loadDate, intlLocale)}
              {d.load.lands ? ` ${d.load.lands}` : ''} · {t('sells')} {formatDate(d.sellFrom, intlLocale)} – {formatDate(d.sellTo, intlLocale)} ({d.load.sells.map((w) => weekdayName(w, intlLocale)).join(', ')})
            </p>
          ) : null}
        </div>
        <div className="seg" role="group" aria-label={t('loadWeek')}>
          <button type="button" data-leaves onClick={() => set({ date: addDays(d?.loadDate ?? date, -7) })}>
            ‹ {t('prevLoad')}
          </button>
          <button type="button" data-leaves aria-pressed={date === todayIso()} onClick={() => set({ date: todayIso() })}>
            {t('nextLoad')}
          </button>
          <button type="button" data-leaves onClick={() => set({ date: addDays(d?.loadDate ?? date, 7) })}>
            {t('laterLoad')} ›
          </button>
        </div>
      </header>
      <nav className="subnav" aria-label={t('vendorTabs')}>
        <a href="#plan" className={tab === 'plan' ? 'active' : undefined} aria-current={tab === 'plan' ? 'page' : undefined} onClick={(e) => { e.preventDefault(); set({ tab: 'plan' }); }}>
          {t('planTab')}
        </a>
        {vendors.map((v) => (
          <a key={v.vendorId} href={`#${v.vendorId}`} className={tab === v.vendorId ? 'active' : undefined} aria-current={tab === v.vendorId ? 'page' : undefined} onClick={(e) => { e.preventDefault(); set({ tab: v.vendorId }); }}>
            {v.vendorName}
            {mark(v)}
          </a>
        ))}
      </nav>
      {sheets.data?.openEarlier ? (
        <div className="warn-band" role="alert" data-testid="open-earlier">
          {t('openEarlier').replace('{date}', formatDate(sheets.data.openEarlier, intlLocale))}{' '}
          <button type="button" data-leaves className="btn btn-secondary btn-sm" onClick={() => set({ date: sheets.data!.openEarlier! })}>
            {t('openEarlierGo')}
          </button>
        </div>
      ) : null}
      {sheets.isError ? <ErrorBox error={sheets.error} /> : null}
      {!load ? null : tab === 'plan' ? (
        <OrderDayPage key={`${date}:${weekday}`} load={weekday} date={date} extra={extra} onExtra={keepCallAheads} />
      ) : current && sheets.data ? (
        <VendorTab key={`${date}:${weekday}:${current.vendorId}`} sheet={current} sheets={sheets.data} date={date} />
      ) : sheets.isPending ? null : (
        <Empty>{t('noResults')}</Empty>
      )}
    </div>
  );
}

type Typed = { order?: string; received?: string; note?: string; cost?: string };
type Row = VendorSheetLine & { typed: Typed | undefined };

/** What the ORDER cell holds: what was typed, else the order's number, else (nothing written yet) what the model says. */
export function orderOf(l: VendorSheetLine, typed: Typed | undefined, status: VendorSheet['status']): number | null {
  if (typed?.order !== undefined) return typed.order.trim() === '' ? 0 : parseNumber(typed.order);
  if (l.ordered !== null) return l.ordered;
  return status === null ? l.model : 0;
}
/** What came: typed (blank = all of it), else what receiving recorded. */
export function receivedOf(l: VendorSheetLine, typed: Typed | undefined, ordered: number): number | null {
  if (typed?.received !== undefined) return typed.received.trim() === '' ? ordered : parseNumber(typed.received);
  return l.received;
}
/**
 * What Receive books for a line not received yet: a typed number, else all of it (the RECEIVED cell left blank is
 * "it all came"; 0 is "none came").
 */
export function receiveQty(l: VendorSheetLine, typed: Typed | undefined): number | null {
  const ordered = l.ordered ?? 0;
  if (typed?.received === undefined || typed.received.trim() === '') return ordered;
  return parseNumber(typed.received);
}
/** "5  Avocados 48 ct" per line, no codes: what goes in the email to the vendor. */
export function emailText(lines: { qty: number; itemName: string }[]): string {
  return lines.filter((l) => l.qty > 0).map((l) => `${l.qty}\t${l.itemName}`).join('\n');
}

/** A vendor's tab for the load: MODEL says, ORDER (pre-filled with the model), PLACE ORDER, RECEIVED, SHORT. */
function VendorTab({ sheet, sheets, date }: { sheet: VendorSheet; sheets: VendorSheets; date: string }) {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const books = me?.role === 'owner' || me?.role === 'office';
  const load = sheets.load.weekday;
  const [typed, setTyped] = useState<Record<string, Typed>>({});
  /** items put on this vendor's tab by hand (a bit of it from this vendor too): a line until the save brings it back */
  const [added, setAdded] = useState<VendorSheetLine[]>([]);
  const [all, setAll] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const receiveKey = useRef<{ body: string; key: string } | null>(null);
  const status = sheet.status;
  // an added line counts once a number is typed on it (Save would drop it otherwise)
  const dirty = Object.keys(typed).length > 0;
  useLeaveGuard(dirty, t('orderDayUnsaved'));

  const done = (fresh: VendorSheets) => {
    qc.setQueryData(qk.vendorSheets(`${date}:${load}`), fresh);
    void qc.invalidateQueries({ queryKey: ['forecast', 'order-history'] });
    void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
  };
  const rows: Row[] = [...sheet.lines, ...added.filter((a) => !sheet.lines.some((l) => l.itemId === a.itemId))].map((l) => ({ ...l, typed: typed[l.itemId] }));
  const addedIds = new Set(added.map((a) => a.itemId));
  const catalog = useQuery({ queryKey: qk.items(''), queryFn: ({ signal }) => api.items('', signal), staleTime: 5 * 60_000, enabled: books && isOpen(status) });
  const addable = (catalog.data ?? []).filter((i) => i.active && !rows.some((r) => r.itemId === i.id)).sort((a, b) => a.name.localeCompare(b.name));
  // the pick list reads "Roma Tomatoes · CS 25 lb · TOM-ROM-25LB": similar packs are told apart
  const pickLabel = (i: (typeof addable)[number]) => [i.name, [i.packUnit, i.packDesc].filter(Boolean).join(' '), i.sku].filter(Boolean).join(' · ');
  const [pickText, setPickText] = useState('');
  const pickTyped = (text: string) => {
    const hit = addable.find((i) => pickLabel(i) === text);
    if (hit) {
      addItem(hit.id);
      setPickText('');
    } else setPickText(text);
  };
  const addItem = (itemId: string) => {
    const i = catalog.data?.find((x) => x.id === itemId);
    if (!i) return;
    setNote(null);
    setAdded((cur) => [...cur, { itemId: i.id, itemName: i.name, code: null, packUnit: i.packUnit, model: 0, ordered: null, received: null, unitCost: null, note: null, offer: null, offeredBy: [], elsewhere: [] }]);
  };
  const costOf = (r: Row): number | null => (r.typed?.cost !== undefined ? parseNumber(r.typed.cost) : r.unitCost);
  // a cost cell cleared: the line follows the vendor's price again (decision 160)
  const cleared = (r: Row) => r.typed?.cost !== undefined && r.typed.cost.trim() === '';
  const bad = rows.some((r) => {
    const o = orderOf(r, r.typed, status);
    const got = receivedOf(r, r.typed, o ?? 0);
    return o === null || o < 0 || (r.typed?.received !== undefined && (got === null || got < 0)) || (r.typed?.cost !== undefined && !cleared(r) && (costOf(r) === null || costOf(r)! < 0));
  });
  const linesToSave = () =>
    rows
      .map((r) => ({
        itemId: r.itemId,
        qty: orderOf(r, r.typed, status) ?? 0,
        ...(r.typed?.note !== undefined ? { note: r.typed.note } : r.note ? { note: r.note } : {}),
        ...(cleared(r) ? { unitCost: null } : r.typed?.cost !== undefined && costOf(r) !== null ? { unitCost: costOf(r)! } : {}),
      }))
      .filter((l) => l.qty > 0);

  /**
   * A typed cost more than 25% from the line's cost (320 for 32): asked once before it is saved, placed or received,
   * because a person's cost moves the auto prices and the open orders at once, never held (decision 165).
   */
  const costsOk = (what: 'order' | 'receive'): boolean => {
    // only the lines this tap sends: ordered ones (a 0 is not saved), or the ones not received yet at Receive
    const sent = (r: Row) => (what === 'receive' ? (r.ordered ?? 0) > 0 && r.received === null : (orderOf(r, r.typed, status) ?? 0) > 0);
    // against the line's cost, else her offer's price (a line added by hand has none)
    const ref = (r: Row) => r.unitCost ?? r.offer?.price ?? null;
    const far = rows.filter((r) => {
      const typedCost = r.typed?.cost !== undefined && !cleared(r) ? costOf(r) : null;
      const was = ref(r);
      return sent(r) && typedCost !== null && was !== null && was > 0 && Math.abs(typedCost - was) / was > 0.25;
    });
    if (far.length === 0) return true;
    const list = far.slice(0, 6).map((r) => `${r.itemName}: ${money(ref(r)!)} → ${money(costOf(r)!)}`).join('\n');
    return window.confirm(t('vsCostFarAsk').replace('{list}', list));
  };
  const save = useMutation({
    mutationFn: async (then: 'save' | 'place') => {
      let fresh = await api.saveVendorSheet(sheet.vendorId, { date, load, lines: linesToSave() });
      if (then === 'place') {
        // a line with no cost places an order the cost cannot follow (decision 160): say which, and ask
        const tab = fresh.vendors.find((v) => v.vendorId === sheet.vendorId);
        const noCost = (tab?.lines ?? []).filter((l) => (l.ordered ?? 0) > 0 && l.unitCost === null).map((l) => l.itemName);
        if (noCost.length && !window.confirm(t('vsNoCostAsk').replace('{n}', String(noCost.length)).replace('{items}', noCost.slice(0, 6).join(', ')))) return { fresh, then: 'save' as const };
        fresh = await api.vendorSheetAction(sheet.vendorId, 'place', { date, load });
      }
      return { fresh, then };
    },
    onSuccess: ({ fresh, then }) => {
      setTyped({});
      setAdded([]);
      done(fresh);
      setNote(then === 'place' ? t('vsPlacedNote') : t('allSaved'));
    },
  });
  const reopen = useMutation({ mutationFn: () => api.vendorSheetAction(sheet.vendorId, 'reopen', { date, load }), onSuccess: (fresh) => { setNote(null); done(fresh); } });
  const receive = useMutation({
    mutationFn: async () => {
      // blank = it all came, 0 = none; what is already received stays as it is
      const lines = rows
        .filter((r) => (r.ordered ?? 0) > 0 && r.received === null)
        // the cost on screen is what the dock records: correct it in the cell when the vendor's price changed
        .map((r) => ({ itemId: r.itemId, qty: receiveQty(r, r.typed) ?? 0, ...(costOf(r) !== null ? { unitCost: costOf(r)! } : {}) }))
        .filter((l) => l.qty > 0);
      if (lines.length > 0) {
        const body = { vendorId: sheet.vendorId, purchaseOrderId: sheet.purchaseOrderId!, freight: 0, fees: 0, allocate: 'packs' as const, lines };
        const serialized = JSON.stringify(body);
        if (receiveKey.current?.body !== serialized) receiveKey.current = { body: serialized, key: newIdempotencyKey() };
        await api.receive(body, receiveKey.current.key);
      }
      return api.vendorSheetAction(sheet.vendorId, 'close', { date, load });
    },
    onSuccess: (fresh) => {
      receiveKey.current = null;
      setTyped({});
      done(fresh);
      setNote(t('vsReceivedNote'));
      // what came changes on hand and ORDER on the plan, the dock price, the open orders on the dashboard and the bills
      for (const key of [['stock'], ['lots'], ['forecast', 'order-day'], ['costs', 'vendor-prices'], ['receipts'], qk.dashboard, qk.payables]) void qc.invalidateQueries({ queryKey: key });
    },
    // the receipt may have gone in before the failure: reload the tab so what came is no longer typable
    onError: () => void qc.invalidateQueries({ queryKey: qk.vendorSheets(`${date}:${load}`) }),
  });
  const busy = save.isPending || reopen.isPending || receive.isPending;

  const words = (r: Row) => orderOf(r, r.typed, status) ?? 0;
  // what the vendor bills: once received, the cases that came (a short is not paid for); before, the cases ordered
  const billed = (r: Row) => {
    if (r.received !== null) return r.received;
    if (isDone(status)) return 0;
    // while receiving: what is typed as received (blank = all of it)
    if (r.typed?.received !== undefined) return receivedOf(r, r.typed, r.ordered ?? 0) ?? 0;
    return words(r);
  };
  const shown = rows.filter((r) => all || r.model > 0 || r.offer !== null || (r.ordered ?? 0) > 0 || r.typed !== undefined || addedIds.has(r.itemId) || r.elsewhere.length > 0);
  const shortOf = (r: Row): number | null => {
    const o = r.ordered ?? 0;
    if (isDone(status)) return r.received === null ? o : Math.max(0, round3(o - r.received));
    if (r.typed?.received !== undefined) {
      const got = receivedOf(r, r.typed, o);
      return got === null ? null : Math.max(0, round3(o - got));
    }
    return r.received !== null && r.received < o ? round3(o - r.received) : null;
  };
  const money = (v: number) => formatMoney(v, intlLocale);
  const edit = (r: Row, change: Typed) => {
    setNote(null);
    setTyped((cur) => {
      const next: Typed = { ...cur[r.itemId], ...change };
      // typed back to what is there is no change
      if (next.order !== undefined && parseNumber(next.order.trim() === '' ? '0' : next.order) === orderOf(r, undefined, status)) delete next.order;
      if (next.note !== undefined && next.note.trim() === (r.note ?? '')) delete next.note;
      if (next.received !== undefined && next.received.trim() === '') delete next.received;
      if (next.cost !== undefined && parseNumber(next.cost) === r.unitCost) delete next.cost;
      const rest = { ...cur };
      delete rest[r.itemId];
      return Object.keys(next).length ? { ...rest, [r.itemId]: next } : rest;
    });
  };
  const canOrder = books && isOpen(status) && !busy;
  /** cases of this item on another vendor's order for the load (what was taken from the offer) */
  // what other vendors' orders for this load carry (the server's count, offer or not)
  const elsewhere = (r: Row, vendorId: string) => r.elsewhere.find((e) => e.vendorId === vendorId)?.qty ?? 0;
  const canReceive = books && isPlaced(status) && !busy;

  // a split item ordered here above what is left to buy: the other vendor's cases would be bought twice
  const overBy = (r: Row) => (isOpen(status) && r.elsewhere.some((e) => e.coming > 0) ? Math.max(0, (orderOf(r, r.typed, status) ?? 0) - r.model) : 0);
  const over = rows.filter((r) => overBy(r) > 0);
  const lowerToModel = () => {
    setNote(null);
    setTyped((cur) => ({ ...cur, ...Object.fromEntries(over.map((r) => [r.itemId, { ...cur[r.itemId], order: String(r.model) }])) }));
  };

  const columns: SheetColumn<Row>[] = [
    { key: 'item', header: t('itemCol'), width: '180px', render: (r) => r.itemName },
    { key: 'code', header: t('vsCode'), width: '80px', render: (r) => r.code ?? '' },
    { key: 'model', header: t('vsModel'), hint: t('vsModelHint'), width: '100px', align: 'right', render: (r) => (r.model ? formatQty(r.model) : '') },
    {
      key: 'offer',
      header: t('vsOfferCol'),
      hint: t('vsOfferHint'),
      width: '130px',
      render: (r) =>
        r.offer ? (
          <span className="offer-own" title={r.offer.note ?? undefined}>
            {t('offeredAt').replace('{price}', r.offer.price !== null ? money(r.offer.price) : '—')}
          </span>
        ) : r.offeredBy.length || r.elsewhere.length ? (
          <span className="offer-other">
            {[
              ...r.offeredBy.map((o) => {
                const there = elsewhere(r, o.vendorId);
                return `${o.vendorName}${o.price !== null ? ` ${money(o.price)}` : ''}${there ? ` · ${t('alsoOrdered').replace('{n}', formatQty(there))}` : ''}`;
              }),
              // ordered from a vendor that sent no offer (bought a bit from each)
              ...r.elsewhere.filter((e) => !r.offeredBy.some((o) => o.vendorId === e.vendorId)).map((e) => `${e.vendorName} · ${t('alsoOrdered').replace('{n}', formatQty(e.qty))}`),
            ].join(', ')}
          </span>
        ) : (
          ''
        ),
      cellClass: (r) => (r.offer ? 'src-offer' : overBy(r) > 0 ? 'warn' : undefined),
    },
    {
      key: 'order',
      header: t('vsOrder'),
      width: '104px',
      align: 'right',
      render: (r) => {
        const o = orderOf(r, r.typed, status);
        return o === null ? r.typed?.order : o ? <strong>{formatQty(o)}</strong> : '';
      },
      edit: { kind: 'number', get: (r) => r.typed?.order ?? (orderOf(r, undefined, status) ? String(orderOf(r, undefined, status)) : ''), set: (r, v) => ({ ...r, typed: { ...r.typed, order: v } }) },
      canEdit: () => canOrder,
      cellClass: (r) => (r.typed?.order !== undefined ? (orderOf(r, r.typed, status) === null ? 'unsaved bad' : overBy(r) > 0 ? 'unsaved warn' : 'unsaved') : overBy(r) > 0 ? 'warn' : status === null && r.model > 0 ? 'src-pattern' : 'order-cell'),
    },
    {
      key: 'received',
      header: t('vsReceivedCol'),
      hint: t('vsReceivedHint'),
      width: '96px',
      align: 'right',
      render: (r) => (r.typed?.received !== undefined ? r.typed.received : r.received !== null ? formatQty(r.received) : ''),
      edit: { kind: 'number', get: (r) => r.typed?.received ?? '', set: (r, v) => ({ ...r, typed: { ...r.typed, received: v } }) },
      canEdit: (r) => canReceive && (r.ordered ?? 0) > 0 && r.received === null,
      cellClass: (r) => (r.typed?.received !== undefined ? (receivedOf(r, r.typed, r.ordered ?? 0) === null ? 'unsaved bad' : 'unsaved') : undefined),
    },
    { key: 'short', header: t('vsShort'), width: '70px', align: 'right', render: (r) => { const s = shortOf(r); return s ? formatQty(s) : ''; }, cellClass: (r) => ((shortOf(r) ?? 0) > 0 ? 'bad' : undefined) },
    {
      key: 'cost',
      header: t('unitCostCol'),
      hint: t('vsCostHint'),
      width: '90px',
      align: 'right',
      render: (r) => (r.typed?.cost !== undefined ? (costOf(r) === null ? r.typed.cost : money(costOf(r)!)) : r.unitCost !== null ? money(r.unitCost) : ''),
      edit: { kind: 'number', get: (r) => r.typed?.cost ?? (r.unitCost !== null ? String(r.unitCost) : ''), set: (r, v) => ({ ...r, typed: { ...r.typed, cost: v } }) },
      canEdit: (r) => canOrder || (canReceive && r.received === null),
      cellClass: (r) => (r.typed?.cost !== undefined ? (costOf(r) === null ? 'unsaved bad' : 'unsaved') : undefined),
    },
    { key: 'total', header: t('lineTotalCol'), width: '100px', align: 'right', render: (r) => (costOf(r) !== null && billed(r) ? money(costOf(r)! * billed(r)) : '') },
    {
      key: 'note',
      header: t('noteCol'),
      width: '140px',
      render: (r) => r.typed?.note ?? r.note ?? '',
      edit: { kind: 'text', get: (r) => r.typed?.note ?? r.note ?? '', set: (r, v) => ({ ...r, typed: { ...r.typed, note: v } }) },
      canEdit: () => canOrder,
      cellClass: (r) => (r.typed?.note !== undefined ? 'unsaved' : undefined),
    },
  ];
  const sum = (f: (r: Row) => number | null) => round3(shown.reduce((s, r) => s + (f(r) ?? 0), 0));
  const cases = sum((r) => orderOf(r, r.typed, status));
  const cost = Math.round(shown.reduce((s, r) => s + (costOf(r) ?? 0) * billed(r), 0) * 100) / 100;
  const footer = (
    <tr>
      <td>{t('totals')}</td>
      <td />
      <td className="al-right">{formatQty(sum((r) => r.model))}</td>
      <td />
      <td className="al-right" data-testid="vs-total-order">{formatQty(cases)}</td>
      <td className="al-right">{formatQty(sum((r) => receivedOf(r, r.typed, r.ordered ?? 0)))}</td>
      <td className="al-right" data-testid="vs-total-short">{formatQty(sum(shortOf))}</td>
      <td />
      <td className="al-right" data-testid="vs-total-cost">{money(cost)}</td>
      <td />
    </tr>
  );
  const copy = async () => {
    const text = emailText(rows.map((r) => ({ qty: orderOf(r, r.typed, status) ?? 0, itemName: r.itemName })));
    try {
      await navigator.clipboard.writeText(text);
      setNote(t('vsCopied'));
    } catch {
      window.prompt(t('vsCopyPrompt'), text);
    }
  };
  const statusLine = isDone(status)
    ? t('vsStatusReceived')
    : isPlaced(status) && sheet.placedAt
      ? t('vsStatusPlaced').replace('{when}', placedLabel(sheet.placedAt, intlLocale)).replace('{who}', sheet.placedBy ?? '')
      : status === 'draft'
        ? t('vsStatusDraft')
        : t('vsStatusNone');

  return (
    <div className="stack-sm">
      <p className="vs-status">
        <strong data-testid="vs-status" className={isPlaced(status) || isDone(status) ? 'good-text' : undefined}>
          {sheet.poNo ? `${sheet.poNo} · ` : ''}
          {statusLine}
        </strong>
      </p>
      <div className="toolbar">
        <label className="tool check">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> {t('vsAllItems')}
        </label>
        {books && isOpen(status) ? (
          <>
            <input className="tool-search" type="search" list={`add-${sheet.vendorId}`} aria-label={t('vsAddItem')} placeholder={`+ ${t('vsAddItem')}`} value={pickText} onChange={(e) => pickTyped(e.target.value)} style={{ maxWidth: 340 }} />
            <datalist id={`add-${sheet.vendorId}`}>
              {addable.map((i) => (
                <option key={i.id} value={pickLabel(i)} />
              ))}
            </datalist>
          </>
        ) : null}
        <span className="spacer" />
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => void copy()} disabled={cases <= 0}>
          {t('vsCopy')}
        </button>
        {books && isOpen(status) ? (
          <>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy || bad || (!dirty && status === 'draft')} onClick={() => costsOk('order') && save.mutate('save')}>
              {t('vsSave')}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy || bad || cases <= 0}
              onClick={() => costsOk('order') && window.confirm(t('vsPlaceConfirm').replace('{n}', formatQty(cases)).replace('{vendor}', sheet.vendorName)) && save.mutate('place')}
            >
              {t('vsPlace')}
            </button>
          </>
        ) : null}
        {books && status === 'sent' && !rows.some((r) => r.received !== null) ? (
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => reopen.mutate()}>
            {t('vsReopen')}
          </button>
        ) : null}
        {canReceive || (books && isPlaced(status) && busy) ? (
          <button type="button" className="btn btn-primary btn-sm" disabled={busy || bad} onClick={() => costsOk('receive') && window.confirm(t('vsReceiveConfirm').replace('{vendor}', sheet.vendorName)) && receive.mutate()}>
            {t('vsReceive')}
          </button>
        ) : null}
      </div>
      {isPlaced(status) ? (
        <p className="muted">{t('vsReceiveHint')}</p>
      ) : isOpen(status) ? (
        <p className="muted">
          {t('vsOrderHint')}
          {rows.some((r) => r.offer) ? ` ${t('vsOfferLinesHint')}` : ''}
        </p>
      ) : null}
      {save.isError ? <ErrorBox error={save.error} /> : null}
      {reopen.isError ? <ErrorBox error={reopen.error} /> : null}
      {over.length ? (
        <div className="warn-band" role="alert" data-testid="vs-over">
          {t('vsOverModel').replace('{items}', over.map((r) => `${r.itemName} (${formatQty(orderOf(r, r.typed, status) ?? 0)} / ${formatQty(r.model)})`).join(', '))}{' '}
          <button type="button" className="btn btn-secondary btn-sm" onClick={lowerToModel} disabled={!books || busy}>
            {t('vsLowerToModel')}
          </button>
        </div>
      ) : null}
      {receive.isError ? <ErrorBox error={receive.error} /> : null}
      {note && !dirty ? (
        <p className="note" role="status">
          {note}
        </p>
      ) : null}
      {shown.length === 0 ? (
        <Empty>{t('vsNothing')}</Empty>
      ) : (
        <Sheet
          label={`${sheet.vendorName} · ${loadName(sheets.load, intlLocale)}`}
          columns={columns}
          rows={shown}
          rowKey={(r) => r.itemId}
          rowLabel={(r) => r.itemName}
          stickyFirst
          minWidth="1090px"
          footer={footer}
          onRowsChange={(next, at) => {
            const r = next[at.row]!;
            const field = at.col === 'order' ? 'order' : at.col === 'received' ? 'received' : at.col === 'cost' ? 'cost' : 'note';
            edit(r, { [field]: r.typed?.[field] ?? '' });
          }}
        />
      )}
    </div>
  );
}

const HISTORY_RANGES = presets('d30', 'd7', 'd90', 'd365');
type HistSort = 'date' | 'vendor' | 'item' | 'ordered' | 'short' | 'total';

/** Every order placed with a vendor, line by line (the Order Guide's ORDER HISTORY), or only what came short (SHORTS). */
export function OrderHistoryPage({ shorts = false }: { shorts?: boolean }) {
  const { t, intlLocale } = useLocale();
  const navigate = useNavigate();
  const period = usePeriod(HISTORY_RANGES);
  const [vendor, setVendor] = useState('');
  const loads = useQuery({ queryKey: qk.orderDays, queryFn: ({ signal }) => api.orderDays(signal), staleTime: 10 * 60_000 });
  const q = useQuery({
    queryKey: qk.orderHistory(`${period.from}:${period.to}:${vendor}:${shorts ? 's' : ''}`),
    queryFn: ({ signal }) => api.orderHistory({ from: period.from, to: period.to, ...(vendor ? { vendor } : {}), shorts }, signal),
  });
  const vendors = useMemo(() => [...new Map((q.data ?? []).map((r) => [r.vendorId, r.vendorName])).entries()].sort((a, b) => a[1].localeCompare(b[1])), [q.data]);
  const loadLabel = (w: number | null) => {
    if (w === null) return t('anyDay');
    const l = loads.data?.find((x) => x.weekday === w);
    return l ? loadName(l, intlLocale) : weekdayName(w, intlLocale);
  };
  const sorters = useMemo(
    () => ({
      date: byText<OrderHistoryRow>((r) => r.placedAt),
      vendor: byText<OrderHistoryRow>((r) => r.vendorName),
      item: byText<OrderHistoryRow>((r) => r.itemName),
      ordered: byNumber<OrderHistoryRow>((r) => r.ordered),
      short: byNumber<OrderHistoryRow>((r) => r.short),
      total: byNumber<OrderHistoryRow>((r) => r.total),
    }),
    [],
  );
  const { sorted, head } = useSort<OrderHistoryRow, HistSort>(sorters, { key: 'date', dir: -1 }, ['date', 'ordered', 'short', 'total']);
  const money = (v: number | null) => (v === null ? '' : formatMoney(v, intlLocale));
  const when = (r: OrderHistoryRow) => (r.placedAt ? placedLabel(r.placedAt, intlLocale) : '');
  const goTo = (r: OrderHistoryRow) => {
    const l = loads.data?.find((x) => x.weekday === r.loadWeekday);
    if (l && r.loadDate) navigate(`/order-day/${loadSlug(l)}?date=${r.loadDate}&tab=${r.vendorId}`);
  };
  const columns: SheetColumn<OrderHistoryRow>[] = [
    { key: 'date', header: head('date', t('placedCol')), label: t('placedCol'), width: '170px', render: when },
    { key: 'vendor', header: head('vendor', t('vendor')), label: t('vendor'), width: '140px', render: (r) => r.vendorName },
    { key: 'load', header: t('loadCol'), width: '110px', render: (r) => `${loadLabel(r.loadWeekday)}${r.loadDate ? ` ${formatDate(r.loadDate, intlLocale)}` : ''}` },
    { key: 'item', header: head('item', t('itemCol')), label: t('itemCol'), width: '200px', render: (r) => r.itemName ?? '' },
    { key: 'code', header: t('vsCode'), width: '90px', render: (r) => r.code ?? '' },
    { key: 'ordered', header: head('ordered', t('orderedCol')), label: t('orderedCol'), width: '80px', align: 'right', render: (r) => formatQty(r.ordered) },
    { key: 'received', header: t('vsReceivedCol'), width: '84px', align: 'right', render: (r) => (r.received === null ? '' : formatQty(r.received)) },
    { key: 'short', header: head('short', t('vsShort')), label: t('vsShort'), width: '70px', align: 'right', render: (r) => (r.short ? formatQty(r.short) : ''), cellClass: (r) => ((r.short ?? 0) > 0 ? 'bad' : undefined) },
    ...(shorts ? [] : [{ key: 'cost', header: t('unitCostCol'), width: '90px', align: 'right' as const, render: (r: OrderHistoryRow) => money(r.unitCost) }]),
    ...(shorts ? [] : [{ key: 'total', header: head('total', t('lineTotalCol')), label: t('lineTotalCol'), width: '100px', align: 'right' as const, render: (r: OrderHistoryRow) => money(r.total) }]),
    { key: 'by', header: t('byCol'), width: '120px', render: (r) => r.placedBy ?? '' },
    { key: 'note', header: t('noteCol'), width: '160px', render: (r) => r.note ?? '' },
  ];
  const rows = sorted(q.data ?? []);
  const shortCases = Math.round(rows.reduce((s, r) => s + (r.short ?? 0), 0) * 1000) / 1000;
  const shortMoney = Math.round(rows.reduce((s, r) => s + (r.short ?? 0) * (r.unitCost ?? 0), 0) * 100) / 100;
  const orderedMoney = Math.round(rows.reduce((s, r) => s + (r.total ?? 0), 0) * 100) / 100;
  const orders = new Set(rows.map((r) => r.purchaseOrderId)).size;
  return (
    <div className="desk-page">
      <header className="desk-head">
        <div>
          <h1>{shorts ? t('shortsTitle') : t('orderHistoryTitle')}</h1>
          <p className="muted">{shorts ? t('shortsHint') : t('orderHistoryHint')}</p>
        </div>
      </header>
      <div className="toolbar">
        <PeriodBar presets={HISTORY_RANGES} period={period} />
        <label className="tool">
          <span>{t('vendor')}</span>
          <select value={vendor} onChange={(e) => setVendor(e.target.value)} aria-label={t('vendor')}>
            <option value="">{t('allVendors')}</option>
            {vendors.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <dl className="totals-strip" data-testid="history-strip">
        {shorts ? null : (
          <div>
            <dt>{t('ordersPlaced')}</dt>
            <dd>{orders}</dd>
          </div>
        )}
        {shorts ? null : (
          <div>
            <dt>{t('orderedValue')}</dt>
            <dd>{money(orderedMoney)}</dd>
          </div>
        )}
        <div>
          <dt>{t('casesShort')}</dt>
          <dd className={shortCases > 0 ? 'bad-text' : undefined}>{formatQty(shortCases)}</dd>
        </div>
        <div>
          <dt>{t('shortValue')}</dt>
          <dd>{money(shortMoney)}</dd>
        </div>
      </dl>
      <QueryView query={q}>
        {() =>
          rows.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <Sheet label={shorts ? t('shortsTitle') : t('orderHistoryTitle')} columns={columns} rows={rows} rowKey={(r, i) => `${r.purchaseOrderId}:${r.itemId ?? ''}:${i}`} rowLabel={(r) => `${r.vendorName} ${r.itemName ?? ''}`} minWidth="1100px" onOpen={goTo} />
          )
        }
      </QueryView>
      <p className="muted">
        <Link to="/order-day">{t('orderDayTitle')}</Link>
      </p>
    </div>
  );
}

type OfferLine = { key: string; itemId: string | null; price: string; note: string; offeredOn: string | null };
let offerSeq = 0;
const blankOffer = (): OfferLine => ({ key: `n${++offerSeq}`, itemId: null, price: '', note: '', offeredOn: null });
const toLines = (o: VendorOffer | undefined): OfferLine[] => [...(o?.lines ?? []).map((l) => ({ key: l.itemId, itemId: l.itemId, price: l.price === null ? '' : String(l.price), note: l.note ?? '', offeredOn: l.offeredOn })), blankOffer()];

/**
 * A vendor's weekly offer (the Order Guide's MENDOZA OFFER): pick each item she texted and type her price per case.
 * On the order day each one gets a line on her tab at her price, left blank, and stays on the home vendor's tab.
 * Placing her order uses the offer; it drops off 36 hours later so the next load starts fresh.
 */
export function OffersPage() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const vendors = useQuery({ queryKey: qk.vendors, queryFn: ({ signal }) => api.vendors(signal) });
  const live = useQuery({ queryKey: qk.vendorOffers, queryFn: ({ signal }) => api.vendorOffers(signal) });
  const items = useQuery({ queryKey: qk.items(''), queryFn: ({ signal }) => api.items('', signal), staleTime: 5 * 60_000 });
  const chosen = params.get('vendor') ?? live.data?.[0]?.vendorId ?? '';
  const offer = useQuery({ queryKey: qk.vendorOffer(chosen), queryFn: ({ signal }) => api.vendorOffer(chosen, signal), enabled: !!chosen });
  /** what is typed, and for which vendor: edits never travel to another vendor */
  const [edits, setEdits] = useState<{ vendorId: string; lines: OfferLine[] } | null>(null);
  const [saved, setSaved] = useState(false);
  const loaded = useMemo(() => toLines(offer.data), [offer.data]);
  const lines = edits && edits.vendorId === chosen ? edits.lines : null;
  const setLines = (next: OfferLine[] | null) => setEdits(next ? { vendorId: chosen, lines: next } : null);
  const shown = lines ?? loaded;
  const dirty = lines !== null;
  // the vendor on screen goes in the address, so clearing its offer does not jump to another one
  useEffect(() => {
    if (!params.get('vendor') && chosen) {
      const p = new URLSearchParams(params);
      p.set('vendor', chosen);
      setParams(p, { replace: true });
    }
  }, [chosen, params, setParams]);
  useLeaveGuard(dirty, t('orderDayUnsaved'));
  const options: PickOption[] = useMemo(() => (items.data ?? []).filter((i) => i.active).map((i) => ({ value: i.id, label: i.name, hint: `${i.packUnit}${i.homeVendor ? ` · ${i.homeVendor}` : ''}`, search: i.packDesc ?? '' })), [items.data]);
  const nameOf = useMemo(() => new Map((items.data ?? []).map((i) => [i.id, i.name])), [items.data]);
  const priceOf = (l: OfferLine) => (l.price.trim() === '' ? null : parseNumber(l.price));
  const filled = shown.filter((l) => l.itemId);
  const twice = new Set(filled.map((l) => l.itemId)).size !== filled.length;
  const bad = twice || filled.some((l) => l.price.trim() !== '' && (priceOf(l) === null || priceOf(l)! < 0));
  const save = useMutation({
    mutationFn: (body: { itemId: string; price: number | null; note?: string }[]) => api.saveVendorOffer(chosen, { lines: body }),
    onSuccess: (fresh) => {
      qc.setQueryData(qk.vendorOffer(chosen), fresh);
      void qc.invalidateQueries({ queryKey: qk.vendorOffers });
      void qc.invalidateQueries({ queryKey: ['forecast', 'vendor-sheets'] });
      void qc.invalidateQueries({ queryKey: ['costs', 'vendor-prices'] });
      setLines(null);
      setSaved(true);
    },
  });
  const submit = (clear = false) => save.mutate(clear ? [] : filled.map((l) => ({ itemId: l.itemId!, price: priceOf(l), ...(l.note.trim() ? { note: l.note.trim() } : {}) })));
  const pickVendor = (id: string) => {
    if (dirty && !window.confirm(t('orderDayUnsaved'))) return;
    setLines(null);
    setSaved(false);
    const p = new URLSearchParams(params);
    p.set('vendor', id);
    setParams(p);
  };
  const columns: SheetColumn<OfferLine>[] = [
    {
      key: 'item',
      header: t('itemCol'),
      width: '300px',
      render: (l) => (l.itemId ? (nameOf.get(l.itemId) ?? '') : <span className="muted">{t('pickProduct')}</span>),
      edit: { kind: 'pick', options, placeholder: t('typeToSearch'), get: () => '', set: (l, v) => ({ ...l, itemId: v === '' ? null : v }) },
      cellClass: (l) => (l.itemId && filled.filter((x) => x.itemId === l.itemId).length > 1 ? 'bad' : undefined),
    },
    {
      key: 'price',
      header: t('herPrice'),
      width: '130px',
      align: 'right',
      render: (l) => (l.price.trim() === '' ? '' : priceOf(l) === null ? l.price : formatMoney(priceOf(l)!, intlLocale)),
      edit: { kind: 'number', get: (l) => l.price, set: (l, v) => ({ ...l, price: v }) },
      canEdit: (l) => !!l.itemId,
      cellClass: (l) => (l.price.trim() !== '' && priceOf(l) === null ? 'bad' : undefined),
    },
    { key: 'note', header: t('noteCol'), width: '260px', render: (l) => l.note, edit: { kind: 'text', get: (l) => l.note, set: (l, v) => ({ ...l, note: v }) }, canEdit: (l) => !!l.itemId },
    {
      key: 'offered',
      header: t('offeredCol'),
      hint: t('offeredHint'),
      width: '200px',
      // good through the end of the day after: yesterday's lines go tonight
      render: (l) => (!l.itemId ? '' : !l.offeredOn || l.offeredOn === todayIso() ? t('offeredToday') : t('offeredYesterday')),
      cellClass: (l) => (l.itemId && l.offeredOn && l.offeredOn < todayIso() ? 'warn' : undefined),
    },
  ];
  const o = offer.data;
  return (
    <div className="desk-page">
      <header className="desk-head">
        <div>
          <h1>{t('offersTodayTitle')}</h1>
          <p className="muted">{t('offersHint')}</p>
        </div>
      </header>
      <div className="toolbar">
        <label className="tool">
          <span>{t('vendor')}</span>
          <select value={chosen} onChange={(e) => pickVendor(e.target.value)} aria-label={t('vendor')}>
            <option value="">{t('pickVendor')}</option>
            {(vendors.data ?? []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
                {live.data?.some((x) => x.vendorId === v.id) ? ' ●' : ''}
              </option>
            ))}
          </select>
        </label>
        <span className="spacer" />
        {o && o.lines.length > 0 ? (
          <button type="button" className="btn btn-ghost btn-sm" disabled={save.isPending} onClick={() => window.confirm(t('clearOfferConfirm')) && submit(true)}>
            {t('clearOffer')}
          </button>
        ) : null}
        <button type="button" className="btn btn-primary btn-sm" disabled={!chosen || !dirty || bad || save.isPending} onClick={() => submit()}>
          {t('saveOffer')}
        </button>
      </div>
      {o ? (
        <p className="muted" data-testid="offer-status">
          {o.enteredAt ? t('offerTyped').replace('{when}', placedLabel(o.enteredAt, intlLocale)).replace('{who}', o.enteredBy ?? '') : t('offerNone')}
          {o.usedAt ? ` · ${t('offerUsed').replace('{when}', placedLabel(o.usedAt, intlLocale))}` : ''}
        </p>
      ) : null}
      {twice ? <p className="warn-band">{t('offerTwice')}</p> : null}
      {save.isError ? <ErrorBox error={save.error} /> : null}
      {saved && !dirty ? (
        <p className="note" role="status">
          {t('offerSaved')}
        </p>
      ) : null}
      {chosen && !offer.isSuccess ? (
        <QueryView query={offer}>{() => null}</QueryView>
      ) : chosen ? (
        <Sheet
          label={t('offersTitle')}
          columns={columns}
          rows={shown}
          rowKey={(l) => l.key}
          rowLabel={(l) => (l.itemId ? (nameOf.get(l.itemId) ?? '') : t('pickProduct'))}
          minWidth="890px"
          onRowsChange={(next) => {
            setSaved(false);
            // always one empty line at the bottom to type the next item into
            const kept = next.filter((l, i) => l.itemId || i === next.length - 1);
            setLines(kept[kept.length - 1]?.itemId ? [...kept, blankOffer()] : kept);
          }}
        />
      ) : (
        <Empty>{t('pickVendor')}</Empty>
      )}
    </div>
  );
}

/** The sidebar's Order day entries: a page per load, then the history and the shorts. */
export function useOrderDayNav(enabled: boolean): { to: string; label: string }[] {
  const { t, intlLocale } = useLocale();
  const loads = useQuery({ queryKey: qk.orderDays, queryFn: ({ signal }) => api.orderDays(signal), staleTime: 10 * 60_000, enabled });
  const list = loads.data ?? [
    { weekday: 2, name: 'Tuesday', lands: null, sells: [3, 4, 5] },
    { weekday: 5, name: 'Friday', lands: null, sells: [6, 7, 1, 2] },
  ];
  return [
    ...list.map((l) => ({ to: `/order-day/${loadSlug(l)}`, label: t('loadOrderTitle').replace('{load}', loadName(l, intlLocale)) })),
    { to: '/order-day/offers', label: t('offersTitle') },
    { to: '/order-day/history', label: t('orderHistoryTitle') },
    { to: '/order-day/shorts', label: t('shortsTitle') },
  ];
}

